import { cn } from '@/lib/utils'
import LinkWithTransition from '@/v2/components/helpers/link-with-transition'
import ResponsiveImage from '@/v2/components/helpers/responsive-image'
import SectionHead from '@/v2/components/sections/landing-pages/section-head'
import { Button } from '@/v2/components/ui/button'
import SectionWrapper from '@/v2/components/wrappers/section-wrapper'
import { staggerContainer } from '@/v2/constants/motion'
import { domAnimation, LazyMotion, m } from 'framer-motion'
import React from 'react'

interface HowServicesWorkCard {
  id: number
  title: string
  description?: string
  points?: string[]
  image?: { src: string; alt: string }
}

interface HowServicesWorkSectionProps {
  title: string
  description?: string
  cards: HowServicesWorkCard[]
  className?: string
  isBgDark?: boolean
  image: { src: string; alt: string }
  sectionTitleClassName?: string
  ctaButton?: { href: string; text: string }
  imgClassName?: string
}

const HowServicesWorkSection: React.FC<HowServicesWorkSectionProps> = props => {
  const { title, description, image, cards, ctaButton, isBgDark, sectionTitleClassName, imgClassName, ...rest } = props

  const cardBackground = isBgDark ? 'bg-[#fff]' : 'bg-[#F9F8F8]'

  return (
    <LazyMotion features={domAnimation}>
      <m.section
        variants={staggerContainer()}
        initial='hidden'
        whileInView='show'
        viewport={{ once: true, amount: 0.25 }}
        className={cn('section-container bg-[#FFFFFF] section-spacing', isBgDark ? 'bg-[#F9F8F8]' : '')}
      >
        <SectionWrapper {...rest}>
          <SectionHead title={title} description={description} className={sectionTitleClassName} />

          <div className='flex items-center gap-[29px] xl-min:flex-row flex-col-reverse'>
            <div className='grid md-mn:grid-cols-2 grid-cols-1 md-mn:gap-5 gap-3 lg-mn:grid-cols-4 xl-min:!grid-cols-2'>
              {cards.map((card, index) => (
                <m.article
                  key={card.id}
                  // className=''
                  className={cn(
                    `flex items-start flex-col py-7 px-4 [&>h3]:text-paragraph
                [&>h3]:font-semibold xl-min:[&>h3]:text-xl md-mn:text-left text-center [&>h3]:w-full [&>h3]:text-lg [&>h3]:mb-3 [&>h3]:mt-5 border-2 border-[#E5E5E5] transition-all rounded-md hover:border-heading-home`,
                    cardBackground
                  )}
                  initial='hidden'
                  whileInView='visible'
                  viewport={{ once: false, amount: 0.3 }}
                  transition={{
                    delay: index * 0.1,
                    duration: 0.6
                  }}
                >
                  {/* in case there is an image */}
                  {card.image && (
                    <div className='md-mn:self-start self-center'>
                      <ResponsiveImage src={card.image.src} alt={card.image.alt} className={'w-[70px] h-[70px]'} />
                    </div>
                  )}
                  {/* in case there is no image */}
                  {!card.image && (
                    <span className='text-heading-home text-5xl font-bold md-mn:self-start self-center'>{card.id}</span>
                  )}
                  <h3>{card.title}</h3>

                  {card.description && (
                    <p className='text-paragraph leading-6 font-normal text-base'>{card.description}</p>
                  )}
                  {card.points && (
                    <ul className='list-outside list-disc ml-7'>
                      {card.points.map((point, pointIdx) => (
                        <li
                          key={pointIdx}
                          dangerouslySetInnerHTML={{ __html: point }}
                          className='text-paragraph font-normal leading-6 text-[16px] break-words text-left'
                        />
                      ))}
                    </ul>
                  )}
                </m.article>
              ))}
            </div>

            <div>
              <ResponsiveImage
                src={image.src}
                alt={image.alt}
                className={cn(
                  'xl-min:w-[737px] xl-min:h-[548px] lg-mn:w-[751px] lg-mn:h-[560px] md-mn:w-[687px] md-mn:h-[512px] w-[338px] h-[252px]',
                  imgClassName
                )}
                imgClassName={imgClassName}
              />
            </div>
          </div>
          {ctaButton && (
            <LinkWithTransition href={ctaButton.href} className='mt-12 flex items-center justify-center'>
              <Button variant='call_to_action' size='lg' className='transition-all animate-bounce self-center'>
                {ctaButton.text}
              </Button>
            </LinkWithTransition>
          )}
        </SectionWrapper>
      </m.section>
    </LazyMotion>
  )
}

export default HowServicesWorkSection
